import { MundpayCouponIcon } from "@/assets/icons/MundpayCouponIcon";
import Badge from "@/components/shared/Badge/Badge";
import { toast } from "@/hooks/use-toast";
import { Copy, Edit, Link2, Trash } from "lucide-react";
import { useState } from "react";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete";
import { useCouponMutations } from "./hooks/useFetchCupons";
import { useTranslation } from "react-i18next";
import { Coupon } from "@/@types/Coupons";
import { formatCurrencyFromCents } from "@/lib/select-options";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import AffiliateLinksDialog from "@/components/shared/AffiliateLinksDialog/AffiliateLinksDialog";
import { AffiliatePromotionLink } from "@/@types/AffiliatePromotionLink";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Figma: traço de 10px, intervalo de 10px, 1px de espessura, `#27272A`. */
const DASHED_LINE = {
  backgroundImage:
    'repeating-linear-gradient(to right, #27272A 0 10px, transparent 10px 20px)',
};

interface CupomCardProps {
  coupon: Coupon;
  /** Moeda em que vale o valor fixo do cupom. */
  currency: string;
  /** Links dos checkouts com `?coupon=`, um por checkout das ofertas em que o cupom vale. */
  couponLinks: AffiliatePromotionLink[];
  /** Cupom inativo ou vencido: o botão de link fica desabilitado. */
  linkable: boolean;
  onEdit: (coupon: Coupon) => void;
  ctaDisabled?: boolean;
}

const CupomCard = ({
  coupon,
  currency,
  couponLinks,
  linkable,
  ctaDisabled,
  onEdit,
}: CupomCardProps) => {
  const { id, name, status, discountType, discount, discountAmount } = coupon;
  const { handleDelete } = useCouponMutations();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLinksDialogOpen, setIsLinksDialogOpen] = useState(false);
  const { t } = useTranslation('common');

  /** Percentual vem como "10.00"; valor fixo, em centavos. */
  const discountLabel =
    discountType === 'percentage'
      ? `${Number(discount ?? 0)}%`
      : formatCurrencyFromCents(discountAmount ?? 0, currency);

  const handleOpenDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDialog = () => setIsDeleteDialogOpen(false);

  const handleConfirmDelete = () => {
    handleDelete(id);
    handleCloseDialog();
  };

  const handleEditCoupon = () => {
    onEdit(coupon);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(name);
      toast({
        variant: 'success',
        description: t('couponsPage.copySuccess'),
      });
    } catch (err) {
      toast({
        variant: 'error',
        description: t('couponsPage.copyError'),
      });
    }
  };

  return (
    <div
      className='bg-zinc-900 relative rounded-xl p-4 w-full shadow-lg'
      data-cy='cupom-row'
    >
      {/*
        As três faixas do Figma têm 28, 18 e 28px, separadas por 13px: com os
        16px de padding a linha cai no meio dos 132px do card, e é por isso que
        os recortes laterais podem se apoiar no centro do próprio card.
      */}
      <span className='absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-black' />
      <span className='absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-black' />

      <div className='flex h-7 items-center justify-between gap-2'>
        <div className='flex h-7 items-center gap-2'>
          <button
            type='button'
            onClick={handleCopy}
            className='flex h-7 items-center gap-2 bg-zinc-800 hover:bg-zinc-700 transition-colors px-2 rounded-md text-zinc-50 text-sm font-medium'
            data-cy='btn-copy-coupon'
          >
            {name}
            <Copy size={18} className='text-zinc-400' />
          </button>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              {/* Botão desabilitado não dispara hover: o tooltip fica no span. */}
              <TooltipTrigger asChild>
                <span className='inline-flex'>
                  <button
                    type='button'
                    onClick={() => setIsLinksDialogOpen(true)}
                    disabled={!linkable}
                    aria-label={t('couponsPage.copyLinkButton')}
                    className='flex h-7 w-7 items-center justify-center bg-zinc-800 hover:bg-zinc-700 transition-colors rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-800'
                    data-cy='btn-copy-coupon-link'
                  >
                    <Link2 size={18} className='text-zinc-400' />
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent className='bg-zinc-900 border-zinc-700 text-zinc-200'>
                {linkable
                  ? t('couponsPage.copyLinkButton')
                  : t('couponsPage.copyLinkDisabled')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <span className='text-zinc-50 text-xl font-semibold whitespace-nowrap'>
          {discountLabel}
        </span>
      </div>

      <div className='flex h-[18px] items-center my-[13px] -mx-1'>
        <div className='h-px flex-grow' style={DASHED_LINE} />
        <span className='flex w-6 justify-center'>
          <MundpayCouponIcon />
        </span>
        <div className='h-px flex-grow' style={DASHED_LINE} />
      </div>

      <div className='flex h-7 items-center justify-between'>
        <Badge status={status as 'inactive' | 'active' | 'blocked'} />
        {ctaDisabled ? null : (
          <CTATable
            items={[
              {
                id: 1,
                label: t('couponsPage.editButton'),
                icon: <Edit size={16} className='mr-2' />,
                action: handleEditCoupon,
              },
              {
                id: 2,
                label: t('couponsPage.deleteButton'),
                icon: <Trash color='#F87171' size={16} className='mr-2' />,
                action: handleOpenDialog,
              },
            ]}
          />
        )}
        <DialogDelete
          isOpen={isDeleteDialogOpen}
          onClose={handleCloseDialog}
          onDelete={handleConfirmDelete}
          title={t('deleteCoupon.title')}
          description={t('deleteCoupon.description')}
        />
        <AffiliateLinksDialog
          open={isLinksDialogOpen}
          onOpenChange={setIsLinksDialogOpen}
          links={couponLinks}
          title={t('couponsPage.linksTitle')}
          emptyMessage={t('couponsPage.linksEmpty')}
          copySuccessMessage={t('couponsPage.linkCopied')}
          showCloseButton
        />
      </div>
    </div>
  );
};

export default CupomCard;
